import { Entity, EntityId, Timestamp } from '@marmon/shared/types/common';
import { CalculationId } from './analysis-calculation.interface';
import { Quad } from '@marmon/shared/types/quad';

export interface Margin {
	percentage: number | null;
	valueInCents: number;
}

export type InvoiceCostType = 'direct' | 'material';

export type InvoiceQuadCalculation = {
	product: number;
	customer: number;
	value: Quad;
};

/**
 * Note that we do not have to track the exact `timePeriod` index that an invoice
 * is apart of. We can query against the invoiceDate in correlation to the time
 * periods set by the user.
 *
 * @note although we could save the timePeriod, every time a user update the time periods
 * it would force us to query against the entire database and reset each of them, whereas
 * by this approach we can avoid that entirely and just make queries when needed.
 */
export interface Invoice<D = Timestamp> extends Entity<D> {
	/**
	 * Firestore document id. New imports use `createInvoiceId()` (hash of
	 * invoice fields + `csvIndex`). CSV-sourced calculations rebuild invoices
	 * from the file and update this same id — it must stay deterministic.
	 * Older analyses may still have random UUID ids and no `csvStoragePath`.
	 */
	id: EntityId;
	/**
	 * Zero-based index of this invoice in the source CSV row order.
	 * Used to resume partial uploads and as an input to `createInvoiceId()`.
	 * Must be the original file row, not the index after segment filtering.
	 */
	csvIndex?: number;
	customer: string;
	product: string;
	segment: string;

	revenueInCents: number;

	costInCents: number;

	invoiceDate: D;

	/**
	 * The margin is a value we can calculate based on outside values.
	 * - Percentage: Save a static percentage in order to quickly show it without needing
	 * 	to perform additional calculations at runtime.
	 * - Value: The exact total value of the contribution margin of this invoice.
	 */
	marginInCents: number;
	marginPercent: number | null;

	/**
	 *  we can reference the created by on the analysis if we need the information
	 */
	createdBy?: never;

	/**
	 * The quad calculation for where this invoice is placed based on the calculation and
	 * what values are determined for it's customer and product counterpart
	 */
	quads: { [key in CalculationId]?: InvoiceQuadCalculation };

	/**
	 * all of the calculations that this invoice should be returned with
	 */
	calculations: CalculationId[];
}

export interface InvoiceRollup<D = Timestamp> extends Omit<
	Entity<D>,
	'deletedAt' | 'createdAt' | 'updatedAt'
> {
	// Redeclare these because as a quartile invoice, these values could actually be set to null
	customer: string | null;
	product: string | null;
	segment: string | null;

	revenueInCents: number;

	costInCents: number;

	/**
	 * The margin is a value we can calculate based on outside values.
	 * - Percentage: Save a static percentage in order to quickly show it without needing
	 * 	to perform additional calculations at runtime.
	 * - Value: The exact total value of the contribution margin of this invoice.
	 */
	marginInCents: number;
	marginPercent: number | null;

	/**
	 * This entities quartile value based on the calculation it's a part of
	 */
	quartile: number;

	/**
	 * This entities quad value based on the calculation it's a part of
	 */
	quad: number;

	/**
	 * The average amount per transaction
	 * revenueInCents / invoices.length
	 */
	transactionAvgInCents: number;

	/**
	 * The amount of transactions that are associated with this rollup
	 */
	transactionCount: number;

	/**
	 * the count of '80' invoices purchased in the opposite entity being expressed here
	 * If the rollup primary is `product`, this needs to be based on `customer`.
	 */
	count80: number;

	/**
	 * The percent of '80' invoices purchased in the opposite entity being expressed here
	 * if the rollup primary is `product`, this needs to be based on `customer`.
	 *
	 * This value is the `count80` divided by the total number of invoices included in the calculation.
	 * @example `count80 / invoices.length`
	 */
	percent80: number;

	/**
	 * the revenue that all of the 80 invoices have aggregated
	 */
	revenueInCents80: number;

	/**
	 * the cost that all of the 80 invoices have aggregated
	 */
	costInCents80: number;

	/**
	 * the margin that all of the 80 invoices have aggregated
	 */
	marginInCents80: number;

	/**
	 * The margin percentage of only the '80's of the opposite entity being expressed here
	 * If the rollup primary is `product`, this needs to be based on `customer`
	 *
	 * (80 Revenue - 80 Cost) / 80 Revenue
	 */
	marginPercent80: number | null;

	/**
	 * the count of '20' invoices purchased in the opposite entity being expressed here
	 * If the rollup primary is `product`, this needs to be based on `customer`.
	 */
	count20: number;

	/**
	 * The percent of '20' invoices purchased in the opposite entity being expressed here
	 * if the rollup primary is `product`, this needs to be based on `customer`.
	 *
	 * This value is the `count20` divided by the total number of invoices included in the calculation.
	 * @example `count20 / invoices.length`
	 */
	percent20: number;

	/**
	 * the revenue that all of the 20 invoices have aggregated
	 */
	revenueInCents20: number;

	/**
	 * the cost that all of the 20 invoices have aggregated
	 */
	costInCents20: number;

	/**
	 * the margin that all of the 20 invoices have aggregated
	 */
	marginInCents20: number;

	/**
	 * The margin percentage of only the '20's of the opposite entity being expressed here
	 * If the rollup primary is `product`, this needs to be based on `customer`
	 *
	 * (20 Revenue - 20 Cost) / 20 Revenue
	 */
	marginPercent20: number | null;

	/**
	 *  we can reference the created by on the calculation if we need the information
	 */
	createdBy?: never;
}

export type InvoiceRollupAverageType =
	| 'products'
	| 'products20'
	| 'products80'
	| 'customers'
	| 'customers20'
	| 'customers80';

export type InvoiceRollupAverage = Omit<
	InvoiceRollup,
	| 'id'
	| 'createdBy'
	| 'quartile'
	| 'quad'
	| 'product'
	| 'customer'
	| 'segment'
>;

/**
 * Used as a temporary measure when trying to gather the information needed to make calculations
 *
 * @note due to the unknown size of the invoice array we cannot save the array to the database,
 * this is for temporary measures while gathering insights and calculations and then it needs to be
 * dropped before saving. `InvoiceRollup` is the better options when using this on the frontend.
 */
export interface InvoiceRollupWithInvoices extends InvoiceRollup {
	invoices: EntityId[];
}

/**
 * Required CSV headers, matched by name in any order. Extra columns may appear
 * before, between, or after these and are ignored, then stripped before Storage.
 */
export const RawInvoiceKeys = [
	/**
	 * The customer that purchased the products for this invoice.
	 * - Alphanumeric
	 */
	'Customer',

	/**
	 * The specific part number that was sold. Will map to our internal "product" value
	 * instead.
	 * - Alphanumeric
	 */
	'Part #',

	/**
	 * The total revenue that the invoice collected. Note that the cost may exceed the two decimal point rule
	 * - Numeric (xxxx.xx)
	 */
	'Revenue',

	/**
	 * The date of the invoice formatted as an excel serial number. Needs to be converted
	 * to a JS date.
	 * - Numeric (xxxxx)
	 */
	'Invoice Date',

	/**
	 * The segment/division of the invoice.
	 * - Alphanumeric
	 */
	'Segment',

	/**
	 * The cost of the invoice, either direct or material. selected by the user during
	 * the creation of the actual invoice. Note that the cost may exceed the two decimal point rule
	 * - Numeric (xxxx.xx)
	 */
	'Cost',
] as const;

/**
 * The raw invoice object is the data that is coming directly from
 * the expected CSV. We may be able to expand on these in the future or
 * make these dynamic and assignable, but for now we need the CSV to be
 * formatted in this fashion in order to parse it correctly.
 */
export type RawInvoiceKey = (typeof RawInvoiceKeys)[number];
export type RawInvoice = { [key in RawInvoiceKey]: string };
